import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { DateRange } from 'react-date-range';
import { format } from 'date-fns';
import {
  HiChevronUp,
  HiChevronDown,
  HiSearch,
  HiFilter,
  HiDownload,
  HiRefresh,
  HiPlus,
  HiChevronLeft,
  HiChevronRight
} from 'react-icons/hi';
import './DataTable.scss';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

export interface Column {
  key: string;
  title: string | React.ReactNode;
  sortable?: boolean;
  filterable?: boolean;
  render?: (value: any, row: any) => React.ReactNode;
  filterValue?: (row: any) => string; // Custom function to extract filterable text from row data
  filterKey?: string;
  filterType?: 'text' | 'dateRange' | 'select';
  filterOptions?: Array<{ label: string; value: string }>;
  filterRender?: () => React.ReactNode;
  width?: string;
  align?: 'left' | 'center' | 'right';
}

export interface DateRangeValue {
  from?: string;
  to?: string;
}

export interface DataTableProps {
  columns: Column[];
  data: any[];
  loading?: boolean;
  searchable?: boolean;
  filterable?: boolean;
  exportable?: boolean;
  refreshable?: boolean;
  addButton?: {
    label: string;
    onClick: () => void;
    permission?: string;
  };
  customActions?: Array<{
    label: string;
    onClick: () => void;
    icon?: React.ComponentType;
    variant?: 'primary' | 'secondary' | 'info' | 'warning' | 'danger';
  }>;
  onRefresh?: () => void;
  onExport?: () => void;
  pageSize?: number;
  pageSizeOptions?: number[]; // Array of page size options
  showPageSizeSelector?: boolean; // Whether to show the page size selector
  paginationMode?: 'client' | 'server';
  currentPage?: number;
  totalItems?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onFilterChange?: (key: string, value: string) => void;
  onDateRangeChange?: (key: string, range: DateRangeValue) => void;
  emptyMessage?: string;
  className?: string;
  rowKey?: (row: any, index: number) => React.Key;
  onRowClick?: (row: any) => void;
  expandedRowKey?: React.Key | null;
  renderExpandedRow?: (row: any) => React.ReactNode;
}

const DataTable: React.FC<DataTableProps> = ({
  columns,
  data,
  loading = false,
  searchable = true,
  filterable = false,
  exportable = false,
  refreshable = false,
  addButton,
  customActions = [],
  onRefresh,
  onExport,
  pageSize = 20,
  pageSizeOptions = [5, 10, 20, 50],
  showPageSizeSelector = true,
  paginationMode = 'client',
  currentPage: currentPageProp,
  totalItems: totalItemsProp,
  onPageChange,
  onPageSizeChange,
  onFilterChange,
  onDateRangeChange,
  emptyMessage,
  className = '',
  rowKey,
  onRowClick,
  expandedRowKey,
  renderExpandedRow
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);
  const [currentPage, setCurrentPage] = useState(currentPageProp ?? 1);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [currentPageSize, setCurrentPageSize] = useState(pageSize);
  const [dateRanges, setDateRanges] = useState<Record<string, { startDate?: Date; endDate?: Date }>>({});
  const [openDateFilterKey, setOpenDateFilterKey] = useState<string | null>(null);
  const dateFilterRef = useRef<HTMLDivElement | null>(null);

  // Update currentPageSize when pageSize prop changes
  useEffect(() => {
    setCurrentPageSize(pageSize);
  }, [pageSize]);

  useEffect(() => {
    if (paginationMode === 'server' && currentPageProp !== undefined) {
      setCurrentPage(currentPageProp);
    }
  }, [paginationMode, currentPageProp]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dateFilterRef.current && !dateFilterRef.current.contains(event.target as Node)) {
        setOpenDateFilterKey(null);
      }
    };

    if (openDateFilterKey) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDateFilterKey]);

  // Filter and search data
  const filteredData = useMemo(() => {
    if (paginationMode === 'server') {
      return [...data];
    }
    let filtered = [...data];

    // Apply search
    if (searchTerm) {
      filtered = filtered.filter(row =>
        columns.some(column => {
          let searchValue: string;
          if (column.filterValue) {
            searchValue = column.filterValue(row);
          } else {
            searchValue = row[column.key]?.toString() || '';
          }
          return searchValue.toLowerCase().includes(searchTerm.toLowerCase());
        })
      );
    }

    // Apply column filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        const column = columns.find(col => col.key === key);
        filtered = filtered.filter(row => {
          let filterValue: string;
          if (column?.filterValue) {
            filterValue = column.filterValue(row);
          } else {
            filterValue = row[key]?.toString() || '';
          }
          return filterValue.toLowerCase().includes(value.toLowerCase());
        });
      }
    });

    return filtered;
  }, [data, searchTerm, filters, columns, paginationMode]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortConfig) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredData, sortConfig]);

  // Paginate data
  const paginatedData = useMemo(() => {
    if (paginationMode === 'server') {
      return sortedData;
    }
    if (currentPageSize === -1) {
      // Show all items
      return sortedData;
    }
    const startIndex = (currentPage - 1) * currentPageSize;
    return sortedData.slice(startIndex, startIndex + currentPageSize);
  }, [sortedData, currentPage, currentPageSize, paginationMode]);

  const totalItems = paginationMode === 'server'
    ? (totalItemsProp ?? sortedData.length)
    : sortedData.length;

  const totalPages = currentPageSize === -1
    ? 1
    : Math.max(1, Math.ceil(totalItems / currentPageSize));

  const updatePage = (page: number) => {
    setCurrentPage(page);
    if (paginationMode === 'server' && onPageChange) {
      onPageChange(page);
    }
  };

  const handleSort = (key: string) => {
    setSortConfig(current => {
      if (current?.key === key) {
        return {
          key,
          direction: current.direction === 'asc' ? 'desc' : 'asc'
        };
      }
      return { key, direction: 'asc' };
    });
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    onFilterChange?.(key, value);
    updatePage(1);
  };

  const handleDateRangeChange = (key: string, startDate?: Date, endDate?: Date) => {
    setDateRanges(prev => ({ ...prev, [key]: { startDate, endDate } }));
    onDateRangeChange?.(key, {
      from: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
      to: endDate ? format(endDate, 'yyyy-MM-dd') : undefined
    });
    updatePage(1);
  };

  const clearDateRange = (key: string) => {
    setDateRanges(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    onDateRangeChange?.(key, { from: undefined, to: undefined });
    updatePage(1);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setCurrentPageSize(newPageSize);
    if (paginationMode === 'server') {
      onPageSizeChange?.(newPageSize);
      updatePage(1);
      return;
    }
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const getSortIcon = (key: string) => {
    if (sortConfig?.key !== key) return null;
    return sortConfig.direction === 'asc' ? <HiChevronUp /> : <HiChevronDown />;
  };

  return (
    <div className={`modern-data-table ${isRTL ? 'rtl' : 'ltr'} ${className}`}>
      {/* Header Actions */}
      <div className="table-header">
        <div className="header-left">
          {/* {searchable && (
            <div className="search-box">
              <HiSearch className="search-icon" />
              <input
                type="text"
                placeholder={t('common.search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
          )} */}
        </div>

        <div className="header-right">
          {/* {filterable && (
            <button className="action-btn secondary">
              <HiFilter />
              <span>{t('common.filter')}</span>
            </button>
          )} */}

          {exportable && (
            <button className="action-btn secondary" onClick={onExport}>
              <HiDownload />
              <span>{t('common.export')}</span>
            </button>
          )}

          {refreshable && (
            <button className="action-btn secondary" onClick={onRefresh}>
              <HiRefresh />
              <span>{t('common.refresh')}</span>
            </button>
          )}

          {customActions.map((action, index) => {
            const IconComponent = action.icon;
            const variant = action.variant || 'secondary';
            return (
              <button
                key={index}
                className={`action-btn ${variant}`}
                onClick={action.onClick}
              >
                {IconComponent && <IconComponent />}
                <span>{action.label}</span>
              </button>
            );
          })}

          {addButton && (
            <button className="action-btn primary" onClick={addButton.onClick}>
              <HiPlus />
              <span>{addButton.label}</span>
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  style={{
                    width: column.width,
                    textAlign: column.align || (isRTL ? 'right' : 'left')
                  }}
                  className={column.sortable ? 'sortable' : ''}
                  onClick={column.sortable ? () => handleSort(column.key) : undefined}
                >
                  <div className="th-content">
                    <span>{column.title}</span>
                    {column.sortable && (
                      <div className="sort-icons">
                        {getSortIcon(column.key)}
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>

            {/* Filter Row */}
            {filterable && (
              <tr className="filter-row">
                {columns.map((column) => (
                  <th key={`filter-${column.key}`}>
                    {column.filterable && (
                      column.filterRender ? (
                        column.filterRender()
                      ) : column.filterType === 'dateRange' ? (
                        <div className="filter-date" ref={openDateFilterKey === (column.filterKey || column.key) ? dateFilterRef : null}>
                          <button
                            type="button"
                            className="filter-date-trigger"
                            onClick={() =>
                              setOpenDateFilterKey(prev =>
                                prev === (column.filterKey || column.key) ? null : (column.filterKey || column.key)
                              )
                            }
                          >
                            <span>
                              {dateRanges[column.filterKey || column.key]?.startDate && dateRanges[column.filterKey || column.key]?.endDate
                                ? `${format(dateRanges[column.filterKey || column.key]!.startDate!, 'MMM dd, yyyy')} - ${format(dateRanges[column.filterKey || column.key]!.endDate!, 'MMM dd, yyyy')}`
                                : t('common.selectDateRange', 'Select range')}
                            </span>
                          </button>
                          {dateRanges[column.filterKey || column.key]?.startDate && (
                            <button
                              type="button"
                              className="filter-date-clear"
                              onClick={() => clearDateRange(column.filterKey || column.key)}
                            >
                              {t('common.clear', 'Clear')}
                            </button>
                          )}
                          {openDateFilterKey === (column.filterKey || column.key) && (
                            <div className="filter-date-popover">
                              <DateRange
                                ranges={[{
                                  startDate: dateRanges[column.filterKey || column.key]?.startDate ?? new Date(),
                                  endDate: dateRanges[column.filterKey || column.key]?.endDate ?? new Date(),
                                  key: 'selection'
                                }]}
                                onChange={(ranges) => {
                                  const selection = ranges.selection;
                                  handleDateRangeChange(
                                    column.filterKey || column.key,
                                    selection.startDate ?? undefined,
                                    selection.endDate ?? undefined
                                  );
                                }}
                                moveRangeOnFirstSelection={false}
                                rangeColors={[isRTL ? '#2f855a' : '#2f6fed']}
                              />
                            </div>
                          )}
                        </div>
                      ) : column.filterType === 'select' ? (
                        <select
                          className="filter-input select"
                          value={filters[column.filterKey || column.key] || ''}
                          onChange={(e) => handleFilterChange(column.filterKey || column.key, e.target.value)}
                        >
                          {column.filterOptions?.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          placeholder={`${t('common.filter')} ${column.title}`}
                          value={filters[column.filterKey || column.key] || ''}
                          onChange={(e) => handleFilterChange(column.filterKey || column.key, e.target.value)}
                          className="filter-input"
                        />
                      )
                    )}
                  </th>
                ))}
              </tr>
            )}
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="loading-cell">
                  <div className="loading-spinner">
                    <div className="spinner"></div>
                    <span>{t('common.loading')}</span>
                  </div>
                </td>
              </tr>
            ) : paginatedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="empty-cell">
                  {emptyMessage || t('common.noDataFound')}
                </td>
              </tr>
            ) : (
              paginatedData.map((row, index) => {
                const key = rowKey ? rowKey(row, index) : index;
                const isExpanded = expandedRowKey !== undefined && expandedRowKey === key;

                return (
                  <React.Fragment key={key}>
                    <tr
                      className={`data-row${onRowClick ? ' clickable-row' : ''}`}
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                    >
                      {columns.map((column) => (
                        <td
                          key={column.key}
                          style={{ textAlign: column.align || (isRTL ? 'right' : 'left') }}
                        >
                          {column.render
                            ? column.render(row[column.key], row)
                            : row[column.key]
                          }
                        </td>
                      ))}
                    </tr>
                    {isExpanded && renderExpandedRow && (
                      <tr className="expanded-row">
                        <td colSpan={columns.length}>{renderExpandedRow(row)}</td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {(totalPages > 1 || showPageSizeSelector) && (
        <div className="table-pagination">
          <div className="pagination-left">
            {showPageSizeSelector && (
              <div className="page-size-selector">
                <label htmlFor="pageSize">{t('common.show')}</label>
                <select
                  id="pageSize"
                  value={currentPageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  className="page-size-select"
                >
                  {pageSizeOptions.map(size => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                  {paginationMode === 'client' && (
                    <option value={-1}>{t('common.all')}</option>
                  )}
                </select>
                <span>{t('common.entries')}</span>
              </div>
            )}

            <div className="pagination-info">
              {currentPageSize === -1 ? (
                <>
                  {t('common.showing')} {t('common.all')} {totalItems} {t('common.items')}
                </>
              ) : (
                <>
                  {t('common.showing')}
                  {totalItems === 0 ? 0 : ((currentPage - 1) * currentPageSize) + 1}
                  {t('common.to')}
                  {Math.min(currentPage * currentPageSize, totalItems)}
                  {t('common.of')}
                  {totalItems}
                  {t('common.items')}
                </>
              )}
            </div>
          </div>

          {totalPages > 1 && (
            <div className="pagination-controls">
              <button
                className="page-btn"
                disabled={currentPage === 1}
                onClick={() => updatePage(1)}
              >
                {t('common.first')}
              </button>

              <button
                className="page-btn"
                disabled={currentPage === 1}
                onClick={() => updatePage(currentPage - 1)}
              >
                {isRTL ? <HiChevronRight /> : <HiChevronLeft />}
              </button>

              <span className="page-info">
                {t('common.page')} {currentPage} {t('common.of')} {totalPages}
              </span>

              <button
                className="page-btn"
                disabled={currentPage === totalPages}
                onClick={() => updatePage(currentPage + 1)}
              >
                {isRTL ? <HiChevronLeft /> : <HiChevronRight />}
              </button>

              <button
                className="page-btn"
                disabled={currentPage === totalPages}
                onClick={() => updatePage(totalPages)}
              >
                {t('common.last')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DataTable;
